import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  IconButton,
  Collapse,
  Chip,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Inventory as InventoryIcon,
  Inventory2 as Inventory2Icon,
  Numbers as NumbersIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  AttachMoney as AttachMoneyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalOffer as LocalOfferIcon,
} from '@mui/icons-material';
import { formatoPesoArgentino } from '../../../../../services/library';

const DistribucionProductos = ({ estadisticas, estadisticasRenglones }) => {
  const [expanded, setExpanded] = useState(false);

  const productosOrdenados = useMemo(() => {
    if (!estadisticasRenglones?.productosDetallados) return [];
    const { productosDetallados, totalProductos } = estadisticasRenglones;
    return [...productosDetallados]
      .sort((a, b) => b.cantidad - a.cantidad)
      .map((producto) => ({
        ...producto,
        porcentaje:
          totalProductos > 0
            ? Math.round((producto.cantidad / totalProductos) * 100)
            : 0,
      }));
  }, [estadisticasRenglones]);

  if (!estadisticasRenglones) {
    return (
      <Card sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <BarChartIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">
            Distribución de Productos
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          py={4}
        >
          No hay datos de productos para mostrar
        </Typography>
      </Card>
    );
  }

  const {
    totalProductos,
    productosUnicosRenglones,
    valorTotal,
    valorEnvases,
    productosPromo,
  } = estadisticasRenglones;

  const statsCards = [
    {
      label: 'Entregas Totales',
      value: estadisticas.totalPedidos,
      icon: <LocalShippingIcon />,
      color: 'primary.main',
    },
    {
      label: 'Clientes Atendidos',
      value: estadisticas.totalClientes,
      icon: <CheckCircleIcon />,
      color: 'secondary.main',
    },
    {
      label: 'Tipos de Producto',
      value: productosUnicosRenglones,
      icon: <InventoryIcon />,
      color: 'secondary.main',
    },
    {
      label: 'Total Unidades',
      value: totalProductos,
      icon: <NumbersIcon />,
      color: 'primary.main',
    },
    // {
    //   label: 'Valor Total Estimado',
    //   value: formatoPesoArgentino(valorTotal),
    //   icon: <AttachMoneyIcon />,
    //   color: 'success.main',
    // },
    // {
    //   label: 'Valor de Envases',
    //   value: formatoPesoArgentino(valorEnvases),
    //   icon: <Inventory2Icon />,
    //   color: 'info.main',
    // },
  ];

  return (
    <Card sx={{ p: 2 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        flexWrap="wrap"
        rowGap={1}
      >
        <Box display="flex" alignItems="center" flexWrap="wrap" rowGap={1}>
          <BarChartIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">
            Distribución de Productos
          </Typography>
          {productosPromo > 0 && (
            <Chip
              icon={<LocalOfferIcon />}
              label={`${productosPromo} renglón${productosPromo !== 1 ? 'es' : ''} en promoción`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ ml: 2 }}
            />
          )}
        </Box>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {/* Resumen general */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statsCards.map((stat) => (
            <Grid item xs={6} sm={6} md={3} key={stat.label}>
              <Paper
                variant="outlined"
                sx={{ textAlign: 'center', height: '100%' }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={1}
                  sx={{ color: stat.color }}
                >
                  {stat.icon}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  gutterBottom
                >
                  {stat.label}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ color: stat.color, fontWeight: 'bold' }}
                >
                  {stat.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Tabla de distribución */}
        <Typography variant="subtitle2" fontWeight="bold" mb={2}>
          Cantidad por Producto
        </Typography>

        {productosOrdenados.length > 0 ? (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 420 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: 48 }}>
                    #
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Producto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Pedidos
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    % del Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    Distribución
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productosOrdenados.map((producto, index) => (
                  <TableRow key={producto.nombre} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="text.secondary"
                      >
                        #{index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {producto.nombre}
                        </Typography>
                        {producto.esPromo && (
                          <Chip
                            label="PROMO"
                            size="small"
                            color="warning"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" fontWeight="bold">
                        {producto.cantidad}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {producto.cantidadPedidos}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body1" color="text.secondary">
                        {producto.porcentaje}%
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <LinearProgress
                        variant="determinate"
                        value={producto.porcentaje}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#1976d2',
                            borderRadius: 5,
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            py={3}
          >
            No hay productos para mostrar
          </Typography>
        )}
      </Collapse>
    </Card>
  );
};

export default DistribucionProductos;
